import { createContext, useContext, useReducer, useEffect } from "react";

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case "ADD_ITEM": {
      const payload = {
        ...action.payload,
        quantity: Number(action.payload.quantity || 1),
        unitPrice: Number(action.payload.unitPrice ?? action.payload.basePrice ?? 0),
      };

      const existing = state.find(
        (i) =>
          i.id === payload.id &&
          i.selectedSize?.id === payload.selectedSize?.id &&
          i.selectedTexture?.id === payload.selectedTexture?.id
      );

      if (existing) {
        return state.map((i) =>
          i === existing ? { ...i, quantity: i.quantity + payload.quantity } : i
        );
      }

      return [...state, payload];
    }

    case "REMOVE_ITEM": {
      return state.filter(
        (i) =>
          !(
            i.id === action.payload.id &&
            i.selectedSize?.id === action.payload.selectedSizeId &&
            i.selectedTexture?.id === action.payload.textureId
          )
      );
    }

    case "UPDATE_QUANTITY": {
      const { id, quantity, selectedSizeId, textureId } = action.payload;

      return state.map((item) => {
        const match =
          item.id === id &&
          item.selectedSize?.id === selectedSizeId &&
          item.selectedTexture?.id === textureId;

        if (!match) return item;

        return {
          ...item,
          quantity: Math.max(1, quantity),
        };
      });
    }
    case "CLEAR_CART":
      return [];

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [cart, dispatch] = useReducer(cartReducer, [], () => {
    try {
      const stored = localStorage.getItem("cart");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (cart.length === 0) {
      localStorage.removeItem("cart");
    } else {
      localStorage.setItem("cart", JSON.stringify(cart));
    }
  }, [cart]);

  const addItem = (product) => dispatch({ type: "ADD_ITEM", payload: product });

  const removeItem = (id, selectedSizeId, textureId) =>
    dispatch({
      type: "REMOVE_ITEM",
      payload: { id, selectedSizeId, textureId },
    });

  const updateQuantity = (id, quantity, selectedSizeId, textureId) =>
    dispatch({
      type: "UPDATE_QUANTITY",
      payload: { id, quantity, selectedSizeId, textureId },
    });

  const clearCart = () => dispatch({ type: "CLEAR_CART" });

  const totalItems = cart.reduce((sum, i) => sum + i.quantity, 0);

  const totalPrice = cart.reduce((sum, i) => {
    const price = Number(i.unitPrice);

    if (isNaN(price)) return sum;

    return sum + price * Number(i.quantity || 0);
  }, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
